/**
 * Per-page SEO + Open Graph metadata helper.
 *
 * This component is the SINGLE source of truth for the route's
 * <title>, description, canonical URL, keywords, Open Graph and Twitter
 * card tags. The static `public/index.html` intentionally ships ZERO
 * og:/twitter:/canonical/description tags — see that file's comment for
 * the full rationale (short version: Helmet appends rather than replaces
 * pre-existing static meta tags, so duplicating them in index.html caused
 * Facebook to read the wrong, generic-homepage tag for every shared URL).
 *
 * Social-crawler rendering:
 *   Production runs an edge crawler-renderer (Cloudflare) that detects
 *   facebookexternalhit / Twitterbot / LinkedInBot / Slackbot / LINE etc.
 *   and serves a fully-rendered DOM snapshot — so the tags below DO reach
 *   social crawlers as route-specific values.
 *
 *   For any UA that bypasses the renderer, there are no OG tags in the
 *   raw HTML. That is intentional: missing tags = no preview, which is
 *   safer than a stale homepage preview pointing at the wrong URL.
 */
import { Helmet } from "react-helmet-async";

const PROD_ORIGIN = "https://nojoenterprise.com";

function abs(url) {
  if (!url) return url;
  if (/^https?:/i.test(url)) return url;
  return `${PROD_ORIGIN}${url.startsWith("/") ? "" : "/"}${url}`;
}

export default function SEO({
  title,
  description,
  keywords,
  path = "/",
  lang = "en",
  image,
  imageAlt,
  type = "website",
  noindex,
}) {
  const ogImage = abs(image || (lang === "ja" ? "/social/nojo-og-ja.png" : "/social/nojo-og-default.png"));
  const ogImageAlt =
    imageAlt ||
    (lang === "ja"
      ? "NoJo Enterprise — モビリティ・記録・デジタル基盤"
      : "NoJo Enterprise — Mobility · Records · Digital Infrastructure");
  const url = abs(path);
  const localePrimary = lang === "ja" ? "ja_JP" : "en_US";
  const localeAlt = lang === "ja" ? "en_US" : "ja_JP";

  return (
    <Helmet>
      <html lang={lang} />
      <title>{title}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      <link rel="canonical" href={url} />
      <link
        rel="alternate"
        hrefLang={lang}
        href={url}
      />
      <link
        rel="alternate"
        hrefLang={lang === "ja" ? "en" : "ja"}
        href={url.replace(`/${lang}`, `/${lang === "ja" ? "en" : "ja"}`)}
      />
      <link
        rel="alternate"
        hrefLang="x-default"
        href={url.replace(`/${lang}`, "/en")}
      />
      {noindex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
      )}

      <meta property="og:type" content={type} />
      <meta property="og:site_name" content="NoJo Enterprise" />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:locale" content={localePrimary} />
      <meta property="og:locale:alternate" content={localeAlt} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:secure_url" content={ogImage} />
      <meta property="og:image:type" content="image/png" />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={ogImageAlt} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />
      <meta name="twitter:image:alt" content={ogImageAlt} />
    </Helmet>
  );
}

/**
 * Pre-built SEO copy for every top-level route, keyed by language.
 * Imported by each page component so the source of truth stays here.
 *
 * Keyword strategy: lead with the highest-intent terms (ETC card Japan,
 * foreign residents ETC, NEXCO toll receipts, MEISAI automation, money
 * exchange Japan), include both English and Japanese aliases where the
 * page is bilingual, and avoid keyword stuffing — Google penalises that.
 */
export const PAGE_SEO = {
  home: {
    en: {
      title: "NoJo Enterprise | Japan ETC Cards, Toll Records & Digital Services",
      description:
        "Independent Yokosuka-based company since 2013. ETC cards for foreign residents, automated toll-receipt records (MEISAI), and a planned money-exchange service. Bilingual EN / JA support.",
      keywords:
        "NoJo Enterprise, NoJo Enterprise Japan, NoJo Enterprise Yokosuka, ETC card Japan, ETC card foreign residents, English ETC card, JapanETCcard, NEXCO toll, ETC toll receipts, MEISAI automation, JapanTollReceipts, money exchange Japan, Yokosuka company, bilingual support Japan, digital infrastructure Japan, 株式会社NoJo Enterprise",
      image: "/social/og-home-en.png",
    },
    ja: {
      title: "NoJo Enterprise株式会社｜ETCカード・ETC利用明細・外貨両替支援",
      description:
        "横須賀本社のNoJo Enterprise株式会社は、外国人向けETCカード支援（JapanETCcard）、ETC利用明細・利用証明書の整理（JapanTollReceipts）、外貨両替関連サービス（JapanChangeMoney）を日本語・英語で運営しています。2013年創業。",
      keywords:
        "NoJo Enterprise, 株式会社NoJo Enterprise, NoJo Enterprise 横須賀, JapanETCcard, JapanTollReceipts, JapanChangeMoney, ETCカード, ETCカード 外国人, ETCカード 英語対応, ETC利用明細, ETC利用証明書, ETC利用記録, MEISAI, ETC利用照会サービス, 法人ETC管理, 外貨両替 日本, 横須賀 外貨両替, 米ドル 円 両替, 英語対応 サービス 日本, 在日外国人 サポート, 日本 デジタルサービス",
      image: "/social/og-home-ja.png",
    },
  },
  about: {
    en: {
      title: "About NoJo Enterprise | Founder's Story, Mission & Yokosuka HQ",
      description:
        "Founded 2013 in Yokosuka. From 420 to 18,000+ customers (June 2026 milestone) solving real problems in Japan — ETC cards for foreign drivers, automated toll-record reporting, and trusted bilingual digital services.",
      keywords:
        "NoJo Enterprise about, Adam Jones founder, NoJo Enterprise mission, NoJo Enterprise values, Yokosuka digital company, ETC card history Japan, foreign residents service Japan, bilingual company Japan, NoJo Enterprise founded 2013, NBC Olympics Tokyo 2020 ETC",
      image: "/social/og-about-en.png",
    },
    ja: {
      title: "会社情報｜NoJo Enterprise株式会社の歩みと使命",
      description:
        "NoJo Enterprise株式会社は、2013年に横須賀で創業しました。日本で暮らす外国人のETCカード支援、ETC利用記録の整理、信頼性の高いバイリンガル運用を続けてきた歩み、使命、価値観、創業者の思いをご紹介します。",
      keywords:
        "NoJo Enterprise 会社情報, NoJo Enterprise 沿革, ジョーンズ アダム 創業者, NoJo Enterprise ミッション, NoJo Enterprise バリュー, 横須賀 IT企業, 外国人 ETC, バイリンガル 企業, 2013年 創業, 東京2020 NBCオリンピック ETC",
      image: "/social/og-about-ja.png",
    },
  },
  technology: {
    en: {
      title: "Technology | NoJo Enterprise — Operating Foundation, Stack & AI Operations",
      description:
        "How NoJo Enterprise builds and operates the three services. Three pillars (mobility, records, digital infrastructure), the FastAPI + React + MongoDB foundation, and the AI-supported workflows that power every interaction.",
      keywords:
        "NoJo Enterprise technology, NoJo Enterprise stack, FastAPI React MongoDB Japan, AI operations company Japan, bilingual platform technology, JapanETCcard technology, JapanTollReceipts technology",
      image: "/social/og-default-en.png",
    },
    ja: {
      title: "テクノロジー | NoJo Enterpriseの技術基盤とAI支援",
      description:
        "NoJo Enterpriseの3つの柱（モビリティ・記録・デジタル基盤）と、FastAPI・React・MongoDBによる運用基盤、AI支援チームによる運用体制をご紹介します。",
      keywords:
        "NoJo Enterprise テクノロジー, NoJo Enterprise 技術, FastAPI React MongoDB, AI支援 日本, 多言語基盤, JapanETCcard 技術, JapanTollReceipts 技術",
      image: "/social/og-default-ja.png",
    },
  },
  trustSecurity: {
    en: {
      title: "Trust & Security | NoJo Enterprise — Data Handling, Audit & Transparency",
      description:
        "NoJo Enterprise's published trust and security commitments — how we handle customer data, the operating values that drive our defaults, audit-log discipline, and minimal data retention across all three operated services.",
      keywords:
        "NoJo Enterprise trust, NoJo Enterprise security, customer data Japan, audit log discipline, data retention policy Japan, bilingual security, JapanETCcard security, JapanTollReceipts security",
      image: "/social/og-default-en.png",
    },
    ja: {
      title: "信頼とセキュリティ | NoJo Enterpriseのデータ取り扱いと透明性",
      description:
        "NoJo Enterpriseが公開している信頼とセキュリティへの取り組み。お客様データの取り扱い、運用上の価値観、監査ログのディシプリン、最小限のデータ保持についてご紹介します。",
      keywords:
        "NoJo Enterprise 信頼, NoJo Enterprise セキュリティ, お客様データ, 監査ログ, データ保持 ポリシー, 多言語 セキュリティ, JapanETCcard セキュリティ, JapanTollReceipts セキュリティ",
      image: "/social/og-default-ja.png",
    },
  },
  whatsNew: {
    en: {
      title: "What's New | NoJo Enterprise — Latest Updates & Releases",
      description:
        "Approved public updates, releases and progress notes from NoJo Enterprise — covering JapanETCcard, JapanTollReceipts, and the wider corporate site.",
      keywords:
        "NoJo Enterprise news, JapanETCcard updates, JapanTollReceipts release notes, ETC card changelog, MEISAI automation updates, NoJo Enterprise progress",
      image: "/social/og-whatsNew-en.png",
    },
    ja: {
      title: "最新情報 | NoJo Enterpriseの更新履歴",
      description:
        "NoJo Enterpriseおよび運営サービス（JapanETCcard・JapanTollReceipts）の公開アップデートとリリース履歴をご確認いただけます。",
      keywords:
        "NoJo Enterprise 最新情報, JapanETCcard アップデート, JapanTollReceipts リリースノート, ETCカード 更新情報, MEISAI 自動化 アップデート",
      image: "/social/og-whatsNew-ja.png",
    },
  },
  roadmap: {
    en: {
      title: "Roadmap | NoJo Enterprise — Where We're Going",
      description:
        "A public roadmap view of approved future plans across NoJo Enterprise — quarterly milestones for JapanETCcard, JapanTollReceipts, and the planned JapanChangeMoney service.",
      keywords:
        "NoJo Enterprise roadmap, JapanETCcard roadmap, JapanTollReceipts roadmap, JapanChangeMoney launch, NEXCO MEISAI integration, ETC card upcoming features",
      image: "/social/og-roadmap-en.png",
    },
    ja: {
      title: "ロードマップ | NoJo Enterpriseの計画",
      description:
        "NoJo Enterpriseおよび運営サービスの今後の取り組み（四半期計画）を公開ロードマップとしてご確認いただけます。",
      keywords:
        "NoJo Enterprise ロードマップ, JapanETCcard 今後, JapanTollReceipts 計画, JapanChangeMoney 公開予定, ETCカード 新機能",
      image: "/social/og-roadmap-ja.png",
    },
  },
  services: {
    en: {
      title: "Services | NoJo Enterprise — ETC Cards, Toll Records & Money Exchange",
      description:
        "Three independently operated NoJo Enterprise services: JapanETCcard (ETC card support for foreign residents and corporates), JapanTollReceipts (automated MEISAI toll-record reporting), JapanChangeMoney (planned).",
      keywords:
        "NoJo Enterprise services, JapanETCcard, JapanTollReceipts, JapanChangeMoney, ETC card Japan, toll receipt automation, MEISAI report, money exchange Japan, foreign residents Japan services",
      image: "/social/og-services-en.png",
    },
    ja: {
      title: "運営サービス一覧｜JapanETCcard・JapanTollReceipts・JapanChangeMoney",
      description:
        "NoJo Enterprise株式会社が運営・開発する3つのサービスをご紹介します。外国人向けETCカード支援（JapanETCcard）、ETC利用明細・利用証明書の整理（JapanTollReceipts）、外貨両替関連サービス（JapanChangeMoney）。",
      keywords:
        "NoJo Enterprise サービス, NoJo Enterprise サービス一覧, JapanETCcard, JapanTollReceipts, JapanChangeMoney, ETCカード 日本, ETC利用明細, ETC利用証明書, MEISAI レポート, 外貨両替 日本, 外貨両替 横須賀, 外国人向け サービス 日本",
      image: "/social/og-services-ja.png",
    },
  },
  corporateProfile: {
    en: {
      title: "Corporate Profile | NoJo Enterprise — Official Company Facts",
      description:
        "Official company profile for NoJo Enterprise Co., Ltd. (株式会社NoJo Enterprise) — established 2013 in Yokosuka, capital ¥50,000,000, registered scope of business and operated services.",
      keywords:
        "NoJo Enterprise corporate profile, 株式会社NoJo Enterprise, NoJo Enterprise capital, NoJo Enterprise registration number, NoJo Enterprise address, Yokosuka company registration, Adam Jones representative director",
      image: "/social/og-corporateProfile-en.png",
    },
    ja: {
      title: "会社概要 | 株式会社NoJo Enterprise 会社概要",
      description:
        "株式会社NoJo Enterpriseの公式会社概要です。所在地、代表者、設立年、資本金、法人番号、事業内容、運営サービスなど、会社の基本情報をご確認いただけます。",
      keywords:
        "NoJo Enterprise 会社概要, 株式会社NoJo Enterprise, NoJo Enterprise 資本金, NoJo Enterprise 法人番号, 横須賀 会社, ジョーンズ アダム 代表取締役",
      image: "/social/og-corporateProfile-ja.png",
    },
  },
  contact: {
    en: {
      title: "Contact NoJo Enterprise — Yokosuka, Japan (EN / JA Support)",
      description:
        "Contact NoJo Enterprise for general, service, partnership or media inquiries. Phone 046-874-7156, email support@nojoenterprise.com. Office in Yokosuka, Kanagawa. EN / JA bilingual reply within 24 hours on business days.",
      keywords:
        "NoJo Enterprise contact, NoJo Enterprise phone, NoJo Enterprise email, NoJo Enterprise Yokosuka office, ETC card support contact, bilingual support Japan, support@nojoenterprise.com",
      image: "/social/og-contact-en.png",
    },
    ja: {
      title: "お問い合わせ | NoJo Enterprise 横須賀",
      description:
        "NoJo Enterpriseへのお問い合わせはこちら。電話 046-874-7156、メール support@nojoenterprise.com、本社所在地：神奈川県横須賀市。営業日であれば24時間以内に日本語・英語でご返信いたします。",
      keywords:
        "NoJo Enterprise お問い合わせ, NoJo Enterprise 電話番号, NoJo Enterprise メール, 横須賀 オフィス, ETCカード サポート 連絡先, 日英 バイリンガル",
      image: "/social/og-contact-ja.png",
    },
  },
  commercialTransactions: {
    en: {
      title: "Commercial Transactions Notice | NoJo Enterprise",
      description:
        "Statutory notice under Japan's Act on Specified Commercial Transactions — NoJo Enterprise legal disclosures, business name, representative, address and contact.",
      keywords:
        "NoJo Enterprise 特定商取引法, commercial transactions notice Japan, NoJo Enterprise legal notice, NoJo Enterprise disclosures, 特商法 表記",
      image: "/social/og-commercialTransactions-en.png",
    },
    ja: {
      title: "特定商取引法に基づく表記 | NoJo Enterprise",
      description:
        "特定商取引法に基づく表記 — 株式会社NoJo Enterpriseの事業者名、代表者、所在地、連絡先などの法定表記をご案内します。",
      keywords:
        "特定商取引法 表記, NoJo Enterprise 特商法, 株式会社NoJo Enterprise 法定表記, 事業者 表記",
      image: "/social/og-commercialTransactions-ja.png",
    },
  },
  privacy: {
    en: {
      title: "Privacy Policy | NoJo Enterprise",
      description:
        "NoJo Enterprise privacy policy — how we collect, use, store and protect personal information across our website and operated services.",
      keywords:
        "NoJo Enterprise privacy policy, personal information protection Japan, data protection NoJo, 個人情報保護方針",
      image: "/social/og-privacy-en.png",
    },
    ja: {
      title: "プライバシーポリシー | NoJo Enterprise",
      description:
        "NoJo Enterpriseのプライバシーポリシー。個人情報の取得、利用、保管、保護に関する方針をご案内します。",
      keywords:
        "NoJo Enterprise プライバシーポリシー, 個人情報保護方針, 個人情報 取り扱い, 株式会社NoJo Enterprise 個人情報",
      image: "/social/og-privacy-ja.png",
    },
  },
  terms: {
    en: {
      title: "Terms of Service | NoJo Enterprise",
      description:
        "Terms governing use of the NoJo Enterprise corporate website and the general framework applicable across our operated services — JapanETCcard, JapanTollReceipts, and JapanChangeMoney.",
      keywords:
        "NoJo Enterprise terms of service, NoJo Enterprise terms of use, JapanETCcard terms, JapanTollReceipts terms, NoJo Enterprise legal terms, 利用規約 NoJo Enterprise",
      image: "/social/og-terms-en.png",
    },
    ja: {
      title: "利用規約 | NoJo Enterprise",
      description:
        "NoJo Enterpriseコーポレートサイトおよび各運営サービスの利用条件をご案内します。サービス別規約がある場合は、各サービスの規約も併せて適用されます。",
      keywords:
        "NoJo Enterprise 利用規約, NoJo Enterprise 利用条件, JapanETCcard 利用規約, JapanTollReceipts 利用規約, 株式会社NoJo Enterprise 利用規約",
      image: "/social/og-terms-ja.png",
    },
  },
  ourLeaders: {
    en: {
      title: "Leadership Team | NoJo Enterprise — Founders, Directors & Operations",
      description:
        "Meet the leadership team behind NoJo Enterprise — founder Adam Jones, and the people running operations, customer support, and engineering across JapanETCcard, JapanTollReceipts and the corporate platform.",
      keywords:
        "NoJo Enterprise leadership, NoJo Enterprise team, Adam Jones founder, NoJo Enterprise directors, JapanETCcard team, JapanTollReceipts team, Yokosuka leadership",
      image: "/social/og-ourLeaders-en.png",
    },
    ja: {
      title: "経営・運営チーム | NoJo Enterprise",
      description:
        "NoJo Enterpriseのリーダーシップチームをご紹介します。創業者ジョーンズ・アダムをはじめ、運営、カスタマーサポート、エンジニアリングを支える、JapanETCcard・JapanTollReceipts・コーポレートプラットフォームの中核メンバー。",
      keywords:
        "NoJo Enterprise リーダーシップ, NoJo Enterprise 経営陣, ジョーンズ アダム 創業者, NoJo Enterprise 取締役, JapanETCcard チーム, 横須賀 リーダー",
      image: "/social/og-ourLeaders-ja.png",
    },
  },
  ourTeam: {
    en: {
      title: "Our AI Operations Team | NoJo Enterprise",
      description:
        "NoJo Enterprise runs a trained five-member AI Operations team — Sora (Director), Rin (QA Lead) and three Senior Analysts — operating every workflow across ETC cards, toll records and customer support.",
      keywords:
        "NoJo Enterprise AI team, AI operations Japan, NoJo Enterprise team, trained AI workflow, ETC card automation team, MEISAI extraction team, Japan AI operations",
      image: "/social/og-ourTeam-en.png",
    },
    ja: {
      title: "AI支援ワークフロー | NoJo Enterprise",
      description:
        "NoJo EnterpriseのAI支援ワークフローをご紹介します。抽出、確認、品質チェック、人による最終確認を組み合わせ、JapanETCcard、JapanTollReceipts、JapanChangeMoneyの運用を支えています。",
      keywords:
        "NoJo Enterprise AI支援, AIワークフロー, AIオペレーション 日本, ETCカード 自動化, MEISAI 確認支援, 人による確認, AI運用 日本",
      image: "/social/og-ourTeam-ja.png",
    },
  },
  testimonials: {
    en: {
      title: "Customer Testimonials | NoJo Enterprise — Real Reviews from Foreign Drivers in Japan",
      description:
        "Read 2,000+ verified customer reviews from foreign residents, U.S. military families, embassy staff and international businesses who use NoJo Enterprise services across Japan.",
      keywords:
        "NoJo Enterprise reviews, JapanETCcard reviews, JapanETCcard testimonials, foreign resident ETC Japan, U.S. military ETC card Japan, embassy ETC Japan, English ETC card review",
      image: "/social/og-testimonials-en.png",
    },
    ja: {
      title: "お客様の声 | JapanETCcard",
      description:
        "日本在住の外国人、米軍関係者とそのご家族、大使館関係者、国際企業など、日本でETCカードを利用するお客様の声をご紹介します。",
      keywords:
        "NoJo Enterprise レビュー, JapanETCcard 口コミ, JapanETCcard お客様の声, 外国人 ETC 日本, 米軍 ETCカード, 大使館 ETC, 英語 ETCカード レビュー",
      image: "/social/og-testimonials-ja.png",
    },
  },
  foundersMessageJEC: {
    en: {
      title: "Founder's Message · JapanETCcard | NoJo Enterprise",
      description:
        "From founder Adam Jones — the real story behind JapanETCcard.com. Twelve nos before the first yes, eight months of engineering, and 18,000+ customers later (a June 2026 milestone): how an English-language ETC service for foreign residents in Japan actually got built.",
      keywords:
        "JapanETCcard founder story, Adam Jones founder, English ETC card history, foreign residents ETC Japan, NEXCO ETC foreign drivers, NoJo Enterprise founding story",
      image: "/social/og-foundersMessageJEC-en.png",
    },
    ja: {
      title: "代表メッセージ | JapanETCcard",
      description:
        "創業者ジョーンズ・アダムからのメッセージ — JapanETCcard.comの本当の物語。12回の「No」、1回の「Yes」、そして8か月の開発と、2026年6月に到達した18,000名超のお客様という節目。在日外国人ドライバー向けの英語対応ETCサービスが、どのように形になっていったのか。",
      keywords:
        "JapanETCcard 創業ストーリー, ジョーンズ アダム 創業者, 英語 ETCカード 歴史, 外国人 ETC 日本, NEXCO ETC 外国人, NoJo Enterprise 創業",
      image: "/social/og-foundersMessageJEC-ja.png",
    },
  },
  foundersMessageJTR: {
    en: {
      title: "Founder's Message · JapanTollReceipts | NoJo Enterprise",
      description:
        "From founder Adam Jones — the practical pain point that became JapanTollReceipts.jp. Why customers were spending weeks chasing month-end MEISAI toll records, and how an automated daily / weekly / monthly report service changed that.",
      keywords:
        "JapanTollReceipts founder story, MEISAI automation history, NEXCO toll receipt automation, Adam Jones founder, toll receipt service Japan, ETC reimbursement Japan",
      image: "/social/og-foundersMessageJTR-en.png",
    },
    ja: {
      title: "代表メッセージ | JapanTollReceipts",
      description:
        "創業者ジョーンズ・アダムからのメッセージ。JapanTollReceipts.jpは、月末のETC利用明細・利用証明書の確認に時間がかかるという実務上の課題から生まれました。",
      keywords:
        "JapanTollReceipts 創業ストーリー, MEISAI 自動化, ETC利用明細, ETC利用証明書, PDF CSV レポート, 通行料金 精算, ジョーンズ アダム 創業者, NoJo Enterprise",
      image: "/social/og-foundersMessageJTR-ja.png",
    },
  },
  servicesJEC: {
    en: {
      title: "JapanETCcard | ETC Card for Foreign Residents in Japan",
      description:
        "Independent ETC card support service for foreign residents, U.S. military families, embassy staff and international companies driving in Japan. Bilingual EN / JA since 2013. 18,000+ customers (June 2026 milestone).",
      keywords:
        "JapanETCcard, ETC card Japan, ETC card foreign residents, English ETC card, ETC card U.S. military Japan, ETC card SOFA Japan, ETC card MOFA Japan, foreign driver ETC Japan, NEXCO ETC support, ETC card application English, ETC card Yokosuka",
      image: "/social/og-servicesJEC-en.png",
    },
    ja: {
      title: "JapanETCcard｜外国人向けETCカード支援サービス",
      description:
        "JapanETCcardは、日本在住の外国人、在日米軍関係者とそのご家族、大使館関係者、国際企業向けの独立系ETCカードサポートサービスです。英語で申し込み・利用サポートに対応し、2013年から日本語・英語で運営しています。",
      keywords:
        "JapanETCcard, ETCカード 外国人, ETCカード 英語対応, ETCカード SOFA, ETCカード 在日米軍, ETCカード 大使館, 外国人ドライバー ETC, ETCカード 申込み 英語, NEXCO ETC 外国人, 日本 高速道路 ETCカード, ETCカード 横須賀, ETCカード 沖縄 外国人",
      image: "/social/og-servicesJEC-ja.png",
    },
  },
  servicesJTR: {
    en: {
      title: "JapanTollReceipts | ETC Toll Records Auto-Delivered (PDF + CSV)",
      description:
        "Automated daily, weekly or monthly ETC usage records and toll receipts delivered to your inbox as PDF and CSV. Independent records-organization service for drivers, fleets and back offices.",
      keywords:
        "JapanTollReceipts, MEISAI automation, NEXCO toll receipt, ETC toll record Japan, ETC reimbursement Japan, fleet management Japan, daily ETC report, monthly toll report Japan, business toll record Japan, ETC PDF CSV, ETC report email",
      image: "/social/og-servicesJTR-en.png",
    },
    ja: {
      title: "JapanTollReceipts｜ETC利用明細・利用証明書の自動配信",
      description:
        "JapanTollReceipts（JTR）は、ETC利用明細・利用証明書をPDF・CSV形式で日次・週次・月次に自動配信する独立系の記録整理サービスです。法人ETC管理、車両管理、経費確認、社内の利用確認をサポートします。",
      keywords:
        "JapanTollReceipts, ETC利用明細, ETC利用証明書, ETC利用記録, ETC利用照会サービス, MEISAI, ETC明細 PDF, ETC明細 CSV, ETC利用明細 自動取得, 法人 ETC 管理, 法人 ETC 利用明細, ETC 経費精算, ETC 車両管理, ETC フリート管理, 複数 ETCカード 管理, ETC 日次レポート, ETC 週次レポート, ETC 月次レポート",
      image: "/social/og-servicesJTR-ja.png",
    },
  },
  servicesJCM: {
    en: {
      title: "JapanChangeMoney | Planned Currency Exchange Service in Japan",
      description:
        "JapanChangeMoney is a planned, transparent foreign currency exchange service from NoJo Enterprise — designed for residents and visitors in Japan, with bilingual support. Currently in development.",
      keywords:
        "JapanChangeMoney, money exchange Japan, currency exchange Japan, foreign currency Japan, USD to JPY exchange, Yokosuka currency exchange, JPY exchange rate, planned exchange service Japan, bilingual money exchange Japan, NoJo Enterprise exchange",
      image: "/social/og-servicesJCM-en.png",
    },
    ja: {
      title: "JapanChangeMoney｜日本の外貨両替・為替情報サービス（準備中）",
      description:
        "JapanChangeMoneyは、NoJo Enterpriseが準備中の外貨両替関連サービスです。米ドル⇔円をはじめとした両替情報、横須賀・基地周辺での外貨両替ニーズ、英語対応に重点を置いて開発を進めています。",
      keywords:
        "JapanChangeMoney, 外貨両替 日本, 外貨両替 横須賀, 米ドル 円 両替, ドル 円 両替 横須賀, 米ドル 両替 日本, 円 ドル 両替, 為替レート 日本, 米ドル 円 レート, 横須賀 基地 近く 両替, 外貨両替 英語対応, 外国人向け 両替 日本, NoJo Enterprise 両替",
      image: "/social/og-servicesJCM-ja.png",
    },
  },
  signboards: {
    en: {
      title: "Signboards | NoJo Enterprise — Outdoor Advertising Across Japan",
      description:
        "Every JapanETCcard signboard location across Japan — three positions on Route 58 in Okinawa, our Yokosuka home base, and our planned nationwide expansion of physical brand presence.",
      keywords:
        "NoJo Enterprise signboards, JapanETCcard signboards, Route 58 Okinawa billboard, Yokosuka signboard, outdoor advertising Japan, ETC card billboard Japan, foreign driver outdoor ads",
      image: "/social/og-default-en.png",
    },
    ja: {
      title: "看板掲出｜JapanETCcardの屋外広告（日本全国）",
      description:
        "NoJo Enterpriseが日本全国で展開するJapanETCcardの看板一覧です。沖縄・国道58号線の3区画、横須賀拠点、今後の全国展開計画を、設置位置・周辺環境とともにご紹介します。",
      keywords:
        "NoJo Enterprise 看板, JapanETCcard 看板, 国道58号線 看板, 沖縄 屋外広告, 横須賀 看板, 日本 屋外広告, ETCカード 看板, 外国人ドライバー 看板",
      image: "/social/og-default-ja.png",
    },
  },
  signboardsMapMainland: {
    en: {
      title: "Mainland Signboard Map | NoJo Enterprise",
      description:
        "Interactive map of JapanETCcard signboard locations across mainland Japan — Yokosuka home base, planned expansion zones, and surrounding arterial routes that put the service in front of drivers.",
      keywords:
        "JapanETCcard signboards mainland, Yokosuka signboard map, NoJo Enterprise mainland signs, Japan outdoor advertising map",
      image: "/social/og-default-en.png",
    },
    ja: {
      title: "本州 詳細マップ｜JapanETCcard看板掲出位置",
      description:
        "JapanETCcardの本州エリアの看板位置を地図でご確認いただけます。横須賀拠点、今後の主要幹線・基地周辺への展開予定など、外国人ドライバーに届く設置位置をまとめています。",
      keywords:
        "JapanETCcard 本州 看板, 横須賀 看板 地図, NoJo Enterprise 本州 看板, 日本 屋外広告 地図",
      image: "/social/og-default-ja.png",
    },
  },
  signboardsMapOkinawa: {
    en: {
      title: "Okinawa Signboard Map | NoJo Enterprise",
      description:
        "Interactive map of JapanETCcard signboard locations along Route 58 in Okinawa — covering Camp Foster, Kadena access, and the main north–south arterial used daily by foreign drivers.",
      keywords:
        "JapanETCcard Okinawa, Okinawa Route 58 billboard, Camp Foster signboard, NoJo Enterprise Okinawa, ETC card Okinawa, Okinawa outdoor advertising",
      image: "/social/og-default-en.png",
    },
    ja: {
      title: "沖縄 詳細マップ｜国道58号線の看板掲出位置",
      description:
        "JapanETCcardの沖縄エリアの看板位置を地図でご確認いただけます。国道58号線沿い、キャンプ・フォスター・嘉手納基地アクセス、外国人ドライバーが日常的に通る区間を中心にまとめています。",
      keywords:
        "JapanETCcard 沖縄, 国道58号線 看板, キャンプフォスター 看板, NoJo Enterprise 沖縄, ETCカード 沖縄 外国人, 沖縄 屋外広告",
      image: "/social/og-default-ja.png",
    },
  },
  careers: {
    en: {
      title: "Careers at NoJo Enterprise | Yokosuka, Japan",
      description:
        "Join NoJo Enterprise in Yokosuka — a bilingual team turning everyday operational pain points (ETC cards, toll records, exchange) into trusted digital services for residents and businesses in Japan.",
      keywords:
        "NoJo Enterprise careers, NoJo Enterprise jobs, Yokosuka jobs, bilingual jobs Japan, Japan ETC company jobs, JapanETCcard careers, JapanTollReceipts careers, work in Yokosuka",
      image: "/social/og-default-en.png",
    },
    ja: {
      title: "採用情報｜NoJo Enterprise株式会社（横須賀本社）",
      description:
        "NoJo Enterprise株式会社は、横須賀本社で、外国人向けETCカード支援・ETC利用明細レポート・外貨両替関連サービスを運営しています。現場の不便を信頼できるデジタルサービスに変える、バイリンガルチームを募集しています。",
      keywords:
        "NoJo Enterprise 採用, NoJo Enterprise 求人, 横須賀 求人, バイリンガル 求人 日本, ETCカード 企業 求人, JapanETCcard 採用, JapanTollReceipts 採用, 英語対応 求人 日本",
      image: "/social/og-default-ja.png",
    },
  },
  sitemap: {
    en: {
      title: "Sitemap | NoJo Enterprise",
      description:
        "Navigate every public page on the NoJo Enterprise corporate website — services, company information, leadership, legal notices, what's new, roadmap and signboards.",
      keywords:
        "NoJo Enterprise sitemap, NoJo Enterprise navigation, NoJo Enterprise site index",
      image: "/social/og-default-en.png",
    },
    ja: {
      title: "サイトマップ｜NoJo Enterprise株式会社",
      description:
        "NoJo Enterprise株式会社のコーポレートサイト全体のサイトマップです。運営サービス、会社情報、リーダーシップ、法定表記、最新情報、ロードマップ、看板掲出のすべての公開ページをご確認いただけます。",
      keywords:
        "NoJo Enterprise サイトマップ, NoJo Enterprise サイト一覧, NoJo Enterprise ナビゲーション",
      image: "/social/og-default-ja.png",
    },
  },
  foundersMessageHub: {
    en: {
      title: "Founder's Message | NoJo Enterprise",
      description:
        "Founder Adam Jones on the real story behind NoJo Enterprise — JapanETCcard, JapanTollReceipts and the practical, customer-led path from Yokosuka 2013 to today.",
      keywords:
        "NoJo Enterprise founder message, Adam Jones founder, NoJo Enterprise founding story, JapanETCcard founder, JapanTollReceipts founder, Yokosuka founder",
      image: "/social/og-default-en.png",
    },
    ja: {
      title: "代表メッセージ｜NoJo Enterprise株式会社",
      description:
        "創業者ジョーンズ・アダムが語る、NoJo Enterpriseの本当の歩み。JapanETCcard、JapanTollReceiptsを通じて、横須賀2013年からお客様の声を起点に積み上げてきた実務の物語をご紹介します。",
      keywords:
        "NoJo Enterprise 代表メッセージ, NoJo Enterprise 創業ストーリー, ジョーンズ アダム 創業者, JapanETCcard 創業者, JapanTollReceipts 創業者, 横須賀 創業",
      image: "/social/og-default-ja.png",
    },
  },
};
